import cn from 'classnames';
import React, { memo } from 'react';
import CheckboxField from '@framework/ui/Organisms/Filters/Checkboxer/CheckboxFieldContainer';
import FilterGroup from '@framework/ui/Organisms/Filters/components/FilterGroup';
import FilterItem from '@framework/ui/Organisms/Filters/components/FilterItem';
import FilterSection from '@framework/ui/Organisms/Filters/components/FilterSection';
import filtersStyles from '@framework/ui/Organisms/Filters/filters.module.scss';
import Checkbox from '@dealroadshow/uikit-checkbox';

interface IProps {
  items: any[],
  checkboxes: any,
  parentKeyPrefix: string,
  sectionId: string,
  sectionTitle: string,
  isVisible: boolean,
  isExpanded: boolean,
  onSectionToggle: () => void,
  filterName: string,
  isFilterChecked: (filterName: string, id: number) => boolean,
  getCheckboxChildren: (checkboxes: any, parentId: number) => any,
  handleParentFilterChange: (checkbox: any, name: string, parentIds: number[]) => void,
  handleFilterByChange: (filterName: string, ids: number[]) => void,
}

function TwoLevelList(props: IProps) {
  const {
    sectionId,
    sectionTitle,
    isVisible,
    filterName,
    parentKeyPrefix,
    isExpanded,
  } = props;
  const parentIds = props.items.map((item) => item[`${ parentKeyPrefix }Id`]);
  return (
    <FilterSection
      sectionId={ sectionId }
      headerComponent={ (
        <CheckboxField
          name={ sectionId }
          dataTest={ sectionId }
          component={ Checkbox }
          label={ sectionTitle }
          supportIndeterminate
          onChange={ (checkbox) => props.handleParentFilterChange(checkbox, filterName, parentIds) }
        />
      ) }
      isVisible={ isVisible }
      onSectionToggle={ props.onSectionToggle }
    >
      { props.items.map((parentFilterItem) => {
        const { canChangeResponse } = parentFilterItem;
        let childrenList = parentFilterItem[`sub${ parentKeyPrefix }List`];
        let parentId = parentFilterItem[`${ parentKeyPrefix }Id`];
        let parentName = parentFilterItem[`${ parentKeyPrefix }Name`];
        let children = [];
        if (childrenList) {
          children = childrenList.map((child) => ({
            id: child[`sub${ parentKeyPrefix }Id`],
            label: child[`sub${ parentKeyPrefix }Name`],
            disabled: !child.canChangeResponse,
          }));
        }
        const checkboxChildren = props.getCheckboxChildren(props.checkboxes, parentId);
        const isAllChildrenDisabled = Object
          .values(checkboxChildren)
          .every((checkbox: any) => checkbox.disabled);
        const activeChildrenCount = Object
          .values(checkboxChildren)
          .filter((checkbox: any) => !checkbox.disabled).length;
        const checkedChildrenCount = Object
          .values(checkboxChildren)
          .filter((checkbox: any) => !checkbox.disabled && checkbox.checked).length;

        return (
          <FilterGroup
            key={ parentId }
            groupId={ parentId }
            itemsCount={ activeChildrenCount }
            selectedItemsCount={ checkedChildrenCount }
            isExpanded={ isExpanded }
            checkboxComponent={ (
              <CheckboxField
                parent={ sectionId }
                name={ parentId }
                dataTest={ parentId }
                component={ Checkbox }
                label={ parentName }
                supportIndeterminate
                disabled={ !canChangeResponse }
                onChange={ (checkbox) => props.handleParentFilterChange(checkbox, filterName, parentIds) }
              />
            ) }
          >
            { children.map((child) => {
              const {
                id,
                label,
                disabled,
              } = child;
              return (
                <FilterItem
                  key={ id }
                  className={ cn(filtersStyles.filterItem, filtersStyles.filterNestedItem) }
                >
                  <CheckboxField
                    parent={ parentId }
                    name={ id }
                    dataTest={ id }
                    component={ Checkbox }
                    label={ label }
                    supportCheckDisabled={ canChangeResponse && isAllChildrenDisabled }
                    disabled={ disabled }
                    onChange={ () => props.handleFilterByChange(filterName, [id]) }
                    defaultChecked={ props.isFilterChecked(filterName, id) }
                  />
                </FilterItem>
              );
            }) }
          </FilterGroup>
        );
      }) }
    </FilterSection>
  );
}

export default memo(TwoLevelList);
